const IMAGES_SELECTOR_ATTR = 'data-image-scroll'
const CAPTION_SELECTOR_ATTR = 'data-caption-scroll'
const IMAGES_SELECTOR = `[${IMAGES_SELECTOR_ATTR}]`
const CAPTIONS_SELECTOR = `[${CAPTION_SELECTOR_ATTR}]`
const ACTIVE_ATTR = 'visible'

/**
 * Defines a custom element that synchronizes scrolling between images and captions.
 */
class MultirowScroll extends HTMLElement {
  constructor() {
    super()

    this.images = this.querySelectorAll(IMAGES_SELECTOR)
    this.captions = this.querySelectorAll(CAPTIONS_SELECTOR)

    this.maxCaptionHeight = 0

    this.handleScroll = this.handleScroll.bind(this)
    this.onScroll = this._throttle(this.handleScroll, 250)
  }

  connectedCallback() {
    window.addEventListener('scroll', this.onScroll)
    this.setCaptionsHeight()
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll)
  }

  /**
   * Sets the height of all captions to the height of the tallest image.
   */
  setCaptionsHeight() {
    this.maxImageHeight = Array.from(this.images).reduce((max, imageEl) => {
      const imageHeight = imageEl.clientHeight
      return Math.max(max, imageHeight)
    }, 0)

    Array.from(this.captions).forEach(captionEl => {
      captionEl.style.height = `${this.maxImageHeight}px`
    })
  }

  /**
   * Handles the scroll event to activate images and captions based on their visibility related to the viewport.
   */
  handleScroll() {
    const threshold = this.maxImageHeight / 2

    let currentImage = null

    for (const image of Array.from(this.images)) {
      const rect = image.getBoundingClientRect()

      if (rect.top <= this.maxImageHeight && rect.bottom > threshold) {
        currentImage = image
        break
      }
    }

    if (currentImage && currentImage !== this.lastLoggedImage) {
      this.lastLoggedImage = currentImage

      this.lastLoggedImage.setAttribute(ACTIVE_ATTR, '')

      const currentImageIndex = Number(currentImage.getAttribute(IMAGES_SELECTOR_ATTR))

      const activeCaption = Array.from(this.captions).find(caption => {
        const captionIndex = Number(caption.getAttribute(CAPTION_SELECTOR_ATTR))

        return currentImageIndex === captionIndex
      })

      if (activeCaption !== undefined) {
        activeCaption.setAttribute(ACTIVE_ATTR, '')

        Array.from(this.images).forEach((imgEl, index) => {
          if (index !== currentImageIndex) {
            imgEl.removeAttribute(ACTIVE_ATTR)
          }
        })

        Array.from(this.captions).forEach((caption, index) => {
          if (index !== currentImageIndex) {
            caption.removeAttribute(ACTIVE_ATTR)
          }
        })
      }
    }
  }

  /**
   * Throttles a given function to limit the rate at which it is executed.
   * @param {Function} func - The function to throttle.
   * @param {number} limit - The time limit (in milliseconds) for the throttle.
   * @returns {Function} - A throttled version of the function.
   */
  _throttle(func, limit) {
    let lastFunc
    let lastRan
    return function () {
      const context = this
      const args = arguments
      if (!lastRan) {
        func.apply(context, args)
        lastRan = Date.now()
      } else {
        clearTimeout(lastFunc)
        lastFunc = setTimeout(function () {
          if ((Date.now() - lastRan) >= limit) {
            func.apply(context, args)
            lastRan = Date.now()
          }
        }, limit - (Date.now() - lastRan))
      }
    }
  }
}

if (!customElements.get('multirow-scroll')) {
  customElements.define('multirow-scroll', MultirowScroll)
}
